import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardMedia,
  Stack,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ShareAssetButton from '../../shared/components/ShareAssetButton';
import type { ShareAssetKind } from '../../shared/components/ShareAssetEmailDialog';
import FilePreviewDialog from './FilePreviewDialog';
import { HighlightPdfCover } from './HighlightCard';
import { buildSafeDownloadFilename } from './fileVisuals';

// ---------------------------------------------------------------------------
// PosterCard
//
// Shared poster-style card used by:
//   • Project EDM page (Uploaded Recently carousel + EDM templates grid)
//   • Upcoming Projects / Commercial listing pages (file kits grid)
//
// All consumers share the exact same visual treatment so the surfaces
// feel cohesive: portrait cover image (fixed responsive height),
// caption-sized date, single-line title, download counter, share icon,
// and a full-width Download CTA. The card is designed for either a
// constrained CSS grid cell or a flex carousel slide.
// ---------------------------------------------------------------------------

/**
 * Build a placeholder cover URL whose stamped text is the EDM's own
 * title (URL-encoded), so a broken / missing thumbnail still tells the
 * user which item the card represents — rather than every fallback
 * showing the generic word "EDM". The 400×600 dimensions match the
 * portrait aspect ratio every poster surface uses, and the brand-blue
 * background keeps the rail visually cohesive when several items fall
 * back simultaneously (e.g. a CDN outage).
 */
export function posterCoverFallback(title: string): string {
  const safe = (title || 'EDM').trim() || 'EDM';
  return `https://placehold.co/400x600/24487a/ffffff/png?text=${encodeURIComponent(safe)}`;
}

/**
 * Backwards-compatible alias. Kept as a no-arg fallback for callers
 * that don't have a title in hand (none today, but exported in case
 * external code referenced the original constant).
 */
export const POSTER_COVER_FALLBACK = posterCoverFallback('EDM');

/** Fixed cover height shared by EDM thumbnails and eBook PDF previews. */
export const POSTER_COVER_HEIGHT = { xs: 220, sm: 240, md: 260 };

/** Common shape every poster surface produces. */
export interface PosterItem {
  id: string;
  title: string;
  /** Pre-formatted human date, e.g. "08 May 2026". */
  date: string;
  /** Absolute URL for the cover image (nullable → fallback). */
  cover: string | null;
  /** @deprecated Unused — kept optional for API payload compatibility. */
  downloads?: number;
  /** Download URL. `'#'` is treated as "not yet downloadable". */
  url: string;
  /**
   * Optional public (no-agent-details) variant of the download URL.
   * When provided the shared FilePreviewDialog surfaces a "With
   * Agent Details" checkbox; when omitted the toggle is hidden.
   */
  urlPublic?: string | null;
  /** Suggested save-as filename. */
  filename: string;
  /**
   * When true, the cover renders a live pdf.js first-page preview
   * (same as Latest Highlights) instead of the `cover` thumbnail.
   */
  pdfPreviewCover?: boolean;
}

interface PosterCardProps {
  item: PosterItem;
  /** How the cover image fills the portrait frame. Default `cover` crops; `contain` shows the full thumbnail. */
  coverFit?: 'cover' | 'contain';
  onPreview?: (item: PosterItem) => void;
  onDownloadClick?: (item: PosterItem) => void;
  shareKind?: ShareAssetKind;
}

export function PosterCard({
  item,
  coverFit = 'cover',
  onPreview,
  onDownloadClick,
  shareKind = 'edm',
}: PosterCardProps) {
  // Click-through opens the shared preview dialog. The dialog's
  // own footer Download button performs the file save — see
  // `FilePreviewDialog` for the full behaviour. Both the cover
  // image and the explicit Download button drive the same handler,
  // so the typing is loosened to any element.
  const [previewOpen, setPreviewOpen] = useState(false);
  const handleOpenPreview = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (onPreview) onPreview(item);
    setPreviewOpen(true);
  };

  // Detect broken cover URLs. We render the cover via CSS
  // `background-image`, which silently swallows 404s / CORS errors —
  // there's no `onError` to hook into. Preload the image with a
  // detached `<img>` and flip to the fallback if it errors, so a
  // missing thumbnail shows the EDM name placeholder instead of an
  // invisible empty box. Resets whenever `item.cover` changes so the
  // same card reused for a new item retries the new URL.
  const [coverFailed, setCoverFailed] = useState(false);
  useEffect(() => {
    setCoverFailed(false);
    if (!item.cover) return;
    const probe = new Image();
    let cancelled = false;
    probe.onerror = () => {
      if (!cancelled) setCoverFailed(true);
    };
    probe.src = item.cover;
    return () => {
      cancelled = true;
      // Detach handlers to avoid late firings on a stale closure.
      probe.onerror = null;
    };
  }, [item.cover]);
  const coverUrl =
    item.cover && !coverFailed ? item.cover : posterCoverFallback(item.title);
  const pdfFallbackImage = item.cover || posterCoverFallback(item.title);

  return (
    <>
    <Card
      sx={{
        width: '100%',
        minWidth: 0,
        height: '100%',
        bgcolor: 'var(--color-surface)',
        color: 'var(--color-text)',
        borderRadius: 2,
        overflow: 'hidden',
        border: '1.5px solid var(--color-border-strongest)',
        boxShadow: 'none',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Cover opens preview; the Download CTA below saves the file. */}
      {item.pdfPreviewCover ? (
        <Box
          component="button"
          type="button"
          onClick={handleOpenPreview}
          aria-label={`Open preview for ${item.title}`}
          sx={{
            all: 'unset',
            display: 'block',
            width: '100%',
            height: POSTER_COVER_HEIGHT,
            maxHeight: POSTER_COVER_HEIGHT,
            flexShrink: 0,
            overflow: 'hidden',
            cursor: 'pointer',
            transition: 'opacity 150ms ease',
            '&:hover': { opacity: 0.9 },
            '&:focus-visible': {
              outline: '2px solid var(--color-accent)',
              outlineOffset: -2,
            },
          }}
        >
          <HighlightPdfCover
            url={item.url}
            fallbackImage={pdfFallbackImage}
            alt={item.title}
            height={POSTER_COVER_HEIGHT}
            frameless
          />
        </Box>
      ) : (
        <CardMedia
          component="button"
          type="button"
          onClick={handleOpenPreview}
          aria-label={`Open preview for ${item.title}`}
          sx={{
            all: 'unset',
            display: 'block',
            width: '100%',
            height: POSTER_COVER_HEIGHT,
            flexShrink: 0,
            cursor: 'pointer',
            bgcolor: coverFit === 'contain' ? 'var(--color-surface-elevated)' : undefined,
            backgroundImage: `url(${coverUrl})`,
            backgroundSize: coverFit,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            transition: 'opacity 150ms ease',
            '&:hover': { opacity: 0.9 },
            '&:focus-visible': {
              outline: '2px solid var(--color-accent)',
              outlineOffset: -2,
            },
          }}
        />
      )}
      <Stack spacing={0.75} sx={{ p: { xs: 1.25, sm: 1.5 }, minWidth: 0, flex: 1 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={0.5}
          sx={{ minWidth: 0 }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'var(--color-text-muted)',
              fontWeight: 600,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.date}
          </Typography>
          {item.url && item.url !== '#' ? (
            <ShareAssetButton
              visible
              asset={{
                id: item.id,
                kind: shareKind,
                title: item.title || item.id,
                filename: item.filename,
                url: item.url,
              }}
            />
          ) : null}
        </Stack>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: 'var(--color-text-strong)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
          title={item.title}
        >
          {item.title}
        </Typography>
        <Button
          onClick={handleOpenPreview}
          variant="contained"
          size="small"
          disableElevation
          startIcon={<DownloadIcon />}
          fullWidth
          sx={{
            mt: 0.5,
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 999,
            bgcolor: 'var(--color-accent)',
            color: '#fff',
            '&:hover': { bgcolor: 'var(--color-accent-strong)' },
          }}
        >
          Download
        </Button>
      </Stack>
    </Card>
    <FilePreviewDialog
      open={previewOpen}
      onClose={() => setPreviewOpen(false)}
      title={item.title}
      url={item.url}
      urlPublic={item.urlPublic ?? null}
      filename={buildSafeDownloadFilename(item.title, item.filename)}
      onDownloadClick={() => {
        if (onDownloadClick) onDownloadClick(item);
      }}
    />
    </>
  );
}

